/**
 * Sheet structure for the public `/one-pagers/<slug>` pages.
 *
 * ## These are NOT the internal sales sheets, and that is the whole point
 *
 * They started as a faithful publication of
 * `docs/sales/icp-one-pagers/{b2b-saas-docs-heavy,hebrew-b2c,niche-enterprise}.md`,
 * and publishing a sales sheet verbatim put the reader's own segment on the
 * screen as a specification of them. "B2B SaaS, 200-2,000 employees, $50M-$1B
 * ARR, paid CS team of 20+" tells a smaller company it does not qualify;
 * "Buyer: VP Customer Success ... procurement gated by a Head of Security
 * review" describes the reader as a gate to get through; and the section keyed
 * `wedge` carried a competitor's pricing strategy ("Fin per-seats them into a
 * budget surprise") under a heading that promises to explain why the product
 * fits. None of that is written for the person reading it.
 *
 * So the two diverged on purpose. The markdown stays the internal sheet:
 * firmographics, buyer titles, competitive positioning, the things a seller
 * needs. The catalog copy answers the same five questions for the reader in
 * their own terms, and a change to the markdown is NOT automatically a change
 * here. Move a fact across only when the reader would want it, and rewrite it
 * when you do.
 *
 * The five sections are stable and reader-shaped: who this is for (situations,
 * never firmographics), what usually hurts, how Syncanix solves it, why it
 * works here, what it costs. The `wedge` key name survives from the original;
 * its heading is "Why it works here" and its content is about the reader.
 *
 * ## i18n
 *
 * This file holds only structure + stable `onepagers.*` translation KEYS, never
 * display prose. The English copy lives in `src/i18n/messages/en.json`; the
 * `OnePager` / `OnePagersIndex` components resolve every key through `useT()`,
 * and the build-time `OnePagerPdf` resolves them through an injected
 * translator. Localized copy lands in the per-locale catalogs, so there is no
 * English here to drift.
 */

export type OnePagerSlug = 'b2b-saas-docs-heavy' | 'hebrew-b2c' | 'niche-enterprise';

export interface OnePagerSection {
  /** `onepagers.*` key for the section heading. */
  readonly headingKey: string;
  /** `onepagers.*` keys for each bullet, in order. */
  readonly bulletKeys: readonly string[];
}

export interface OnePager {
  readonly slug: OnePagerSlug;
  /** `onepagers.*` key for the eyebrow naming the kind of company. */
  readonly archetypeKey: string;
  /** `onepagers.*` key for the page H1. */
  readonly titleKey: string;
  /** `onepagers.*` key for the short list-card summary on the index. */
  readonly summaryKey: string;
  /** `onepagers.*` key for the opening paragraph. */
  readonly ledeKey: string;
  readonly sections: readonly OnePagerSection[];
}

export const ONE_PAGERS: Record<OnePagerSlug, OnePager> = {
  'b2b-saas-docs-heavy': {
    slug: 'b2b-saas-docs-heavy',
    archetypeKey: 'onepagers.b2bSaasDocsHeavy.archetype',
    titleKey: 'onepagers.b2bSaasDocsHeavy.title',
    summaryKey: 'onepagers.b2bSaasDocsHeavy.summary',
    ledeKey: 'onepagers.b2bSaasDocsHeavy.lede',
    sections: [
      {
        headingKey: 'onepagers.b2bSaasDocsHeavy.whoItIs.heading',
        bulletKeys: [
          'onepagers.b2bSaasDocsHeavy.whoItIs.bullet1',
          'onepagers.b2bSaasDocsHeavy.whoItIs.bullet2',
          'onepagers.b2bSaasDocsHeavy.whoItIs.bullet3',
        ],
      },
      {
        headingKey: 'onepagers.b2bSaasDocsHeavy.pain.heading',
        bulletKeys: [
          'onepagers.b2bSaasDocsHeavy.pain.bullet1',
          'onepagers.b2bSaasDocsHeavy.pain.bullet2',
          'onepagers.b2bSaasDocsHeavy.pain.bullet3',
          'onepagers.b2bSaasDocsHeavy.pain.bullet4',
        ],
      },
      {
        headingKey: 'onepagers.b2bSaasDocsHeavy.solution.heading',
        bulletKeys: [
          'onepagers.b2bSaasDocsHeavy.solution.bullet1',
          'onepagers.b2bSaasDocsHeavy.solution.bullet2',
          'onepagers.b2bSaasDocsHeavy.solution.bullet3',
          'onepagers.b2bSaasDocsHeavy.solution.bullet4',
          'onepagers.b2bSaasDocsHeavy.solution.bullet5',
        ],
      },
      {
        headingKey: 'onepagers.b2bSaasDocsHeavy.wedge.heading',
        bulletKeys: [
          'onepagers.b2bSaasDocsHeavy.wedge.bullet1',
          'onepagers.b2bSaasDocsHeavy.wedge.bullet2',
        ],
      },
      {
        headingKey: 'onepagers.b2bSaasDocsHeavy.pricing.heading',
        bulletKeys: [
          'onepagers.b2bSaasDocsHeavy.pricing.bullet1',
          'onepagers.b2bSaasDocsHeavy.pricing.bullet2',
          'onepagers.b2bSaasDocsHeavy.pricing.bullet3',
          'onepagers.b2bSaasDocsHeavy.pricing.bullet4',
        ],
      },
    ],
  },

  'hebrew-b2c': {
    slug: 'hebrew-b2c',
    archetypeKey: 'onepagers.hebrewB2c.archetype',
    titleKey: 'onepagers.hebrewB2c.title',
    summaryKey: 'onepagers.hebrewB2c.summary',
    ledeKey: 'onepagers.hebrewB2c.lede',
    sections: [
      {
        headingKey: 'onepagers.hebrewB2c.whoItIs.heading',
        bulletKeys: [
          'onepagers.hebrewB2c.whoItIs.bullet1',
          'onepagers.hebrewB2c.whoItIs.bullet2',
          'onepagers.hebrewB2c.whoItIs.bullet3',
        ],
      },
      {
        headingKey: 'onepagers.hebrewB2c.pain.heading',
        bulletKeys: [
          'onepagers.hebrewB2c.pain.bullet1',
          'onepagers.hebrewB2c.pain.bullet2',
          'onepagers.hebrewB2c.pain.bullet3',
          'onepagers.hebrewB2c.pain.bullet4',
        ],
      },
      {
        headingKey: 'onepagers.hebrewB2c.solution.heading',
        bulletKeys: [
          'onepagers.hebrewB2c.solution.bullet1',
          'onepagers.hebrewB2c.solution.bullet2',
          'onepagers.hebrewB2c.solution.bullet3',
          'onepagers.hebrewB2c.solution.bullet4',
          'onepagers.hebrewB2c.solution.bullet5',
        ],
      },
      {
        headingKey: 'onepagers.hebrewB2c.wedge.heading',
        bulletKeys: ['onepagers.hebrewB2c.wedge.bullet1', 'onepagers.hebrewB2c.wedge.bullet2'],
      },
      {
        headingKey: 'onepagers.hebrewB2c.pricing.heading',
        bulletKeys: [
          'onepagers.hebrewB2c.pricing.bullet1',
          'onepagers.hebrewB2c.pricing.bullet2',
          'onepagers.hebrewB2c.pricing.bullet3',
          'onepagers.hebrewB2c.pricing.bullet4',
        ],
      },
    ],
  },

  'niche-enterprise': {
    slug: 'niche-enterprise',
    archetypeKey: 'onepagers.nicheEnterprise.archetype',
    titleKey: 'onepagers.nicheEnterprise.title',
    summaryKey: 'onepagers.nicheEnterprise.summary',
    ledeKey: 'onepagers.nicheEnterprise.lede',
    sections: [
      {
        headingKey: 'onepagers.nicheEnterprise.whoItIs.heading',
        bulletKeys: [
          'onepagers.nicheEnterprise.whoItIs.bullet1',
          'onepagers.nicheEnterprise.whoItIs.bullet2',
          'onepagers.nicheEnterprise.whoItIs.bullet3',
          'onepagers.nicheEnterprise.whoItIs.bullet4',
        ],
      },
      {
        headingKey: 'onepagers.nicheEnterprise.pain.heading',
        bulletKeys: [
          'onepagers.nicheEnterprise.pain.bullet1',
          'onepagers.nicheEnterprise.pain.bullet2',
          'onepagers.nicheEnterprise.pain.bullet3',
          'onepagers.nicheEnterprise.pain.bullet4',
        ],
      },
      {
        headingKey: 'onepagers.nicheEnterprise.solution.heading',
        bulletKeys: [
          'onepagers.nicheEnterprise.solution.bullet1',
          'onepagers.nicheEnterprise.solution.bullet2',
          'onepagers.nicheEnterprise.solution.bullet3',
          'onepagers.nicheEnterprise.solution.bullet4',
          'onepagers.nicheEnterprise.solution.bullet5',
        ],
      },
      {
        headingKey: 'onepagers.nicheEnterprise.wedge.heading',
        bulletKeys: [
          'onepagers.nicheEnterprise.wedge.bullet1',
          'onepagers.nicheEnterprise.wedge.bullet2',
        ],
      },
      {
        headingKey: 'onepagers.nicheEnterprise.pricing.heading',
        bulletKeys: [
          'onepagers.nicheEnterprise.pricing.bullet1',
          'onepagers.nicheEnterprise.pricing.bullet2',
        ],
      },
    ],
  },
};

/** Stable display order for the index page. */
/**
 * Render order — **A, B, C**, matching the archetype letters the sheets label
 * themselves with.
 *
 * It read B, A, C, because the array happened to list the B2B sheet first and
 * the letters live in the catalog rather than here. On a page whose whole
 * audience is the reader who cares which archetype they are, three cards
 * labelled out of sequence is not a nit.
 */
export const ONE_PAGER_ORDER: readonly OnePagerSlug[] = [
  'hebrew-b2c', // Archetype A
  'b2b-saas-docs-heavy', // Archetype B
  'niche-enterprise', // Archetype C
];

export function getOnePager(slug: string): OnePager | undefined {
  return (ONE_PAGERS as Record<string, OnePager>)[slug];
}
